import { Box, Container, Typography, Stack, Grid, Card } from "@mui/material"
import React, { useRef, useLayoutEffect } from "react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useIsMobile } from '@/hooks/Responsive'
import img1 from '@assets/images/sub/img_platform_1.png'
import img2 from '@assets/images/sub/img_platform_2.png'
import img3 from '@assets/images/sub/img_platform_3.png'
import PlatformMotion from './PlatformMotion'
import Bg from '@assets/images/sub/bg_banner.png'
import ico1 from '@assets/images/sub/ico_platform_1.svg'
import ico2 from '@assets/images/sub/ico_platform_2.svg'
import ico3 from '@assets/images/sub/ico_platform_3.svg'
import ico4 from '@assets/images/sub/ico_platform_4.svg'
import ico5 from '@assets/images/sub/ico_platform_5.svg'
import { useLang } from '@/i18n/useLang'
import { translateText } from '@/i18n/translate';

gsap.registerPlugin(ScrollTrigger)

export default function Platform() {
  const { isMobile } = useIsMobile()
  const lang = useLang()

  const platform = [
    { title: 'HMD', description: '눈으로 보고, 몸으로 배우는\nXR 학습의 현장', image: img1 },
    { title: 'PC', description: '학습 데이터를 한눈에 조망하는\n학습의 본부', image: img2 },
    { title: 'Mobile', description: '언제 어디서나 연결되는\n학습의 일상', image: img3 },
  ]

  const imgRefs = useRef([])

  useLayoutEffect(() => {
    if (isMobile) {
      imgRefs.current.forEach(el => {
        if (!el) return
        el.style.opacity = 1
        el.style.transform = 'translateY(0px)'
      })
      return
    }

    const ctx = gsap.context(() => {
      gsap.set(imgRefs.current, { y: 70, opacity: 0 })
      gsap.to(imgRefs.current, {
        y: 0,
        opacity: 1,
        duration: 1.2,
        ease: 'power2.out',
        stagger: 0.25,
        scrollTrigger: {
          trigger: imgRefs.current[0],
          start: 'top 80%',
          end: 'top 40%',
          scrub: 1.2,
        },
      })
    })

    return () => ctx.revert()
  }, [isMobile, lang])

  const textLines = [
    '대학교 등 고등교육기관에서 필요한',
    '다양한 분야의 콘텐츠를',
    '공유 및 활용이 가능합니다.',
  ];

  const containerRef = useRef(null)
  const lettersRef = useRef([])

  useLayoutEffect(() => {
    if (isMobile) return

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 80%',
          end: 'top 20%',
          scrub: 0.5,
        },
      })

      lettersRef.current.forEach((line, i) => {
        tl.to(
          line,
          {
            color: '#131416',
            duration: 1,
            ease: 'power2.out',
            stagger: 0.03,
          },
          i * 0.4
        )
      })
    })

    return () => ctx.revert()
  }, [isMobile, lang])

  const features = [
    { title: '생활/재난 안전', description: '대피 구조 순서 훈련', icon: ico1, },
    { title: '화학 실험 실습', description: '위험한 시약 없이 진행', icon: ico2, },
    { title: '산업안전', description: '사고 재현과 대처 훈련', icon: ico3, },
    { title: '물리 치료', description: '동작을 따라하며 재활 체험', icon: ico4, },
    { title: '반도체 공정 견학', description: '클린룸 공정 가상 투어', icon: ico5, },
  ]

  const featuresRef = useRef(null);
  const cardsRef = useRef([]);

  useLayoutEffect(() => {
    if (isMobile) return

    const ctx = gsap.context(() => {
      gsap.set(featuresRef.current, {
        y: 0,
        opacity: 1,
      })

      gsap.to(featuresRef.current, {
        y: 0,
        opacity: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: featuresRef.current,
          start: 'top 85%',
          end: 'top 20%',
          scrub: 1.2,
        },
      })
    })

    return () => ctx.revert()
  }, [isMobile, lang])

  const bannerRef = useRef(null)
  
  useLayoutEffect(() => {
    if (isMobile) return

    const ctx = gsap.context(() => {
      gsap.set(bannerRef.current, {
        y: 0,
        opacity: 1,
      })

      gsap.to(bannerRef.current, {
        y: 0,
        opacity: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: bannerRef.current,
          start: 'top 85%',
          end: 'top 40%',
          scrub: 1.2,
        },
      })
    })

    return () => ctx.revert()
  }, [isMobile, lang])

  return (
    <Box>
      <Container maxWidth="xl" disableGutters>
        <Stack
          direction={isMobile || lang === 'en' ? 'column' : 'row'}
          justifyContent="space-between"
          sx={{ mb: isMobile ? 12 : 24 }}
        >
          <Typography variant={isMobile ? 'h3' : 'h1'} sx={{ letterSpacing: '-0.04em', lineHeight: '1.3' }}>
            실감형 학습콘텐츠
            <Box component="span" sx={{ display: 'block' }}>
              공유 플랫폼
            </Box>
          </Typography>

          <Box sx={{ py: 2, maxWidth: isMobile || lang === 'en' ? 'initial' : '408px' }}>
            <Typography component="p" variant={isMobile ? 'subtitle3' : 'subtitle2'} fontWeight={400} sx={{ mb: 1.5 }}>
              고비용, 고위험, 특수환경의 물리적 실험·실습 환경을 대체하는 가상 실험·실습·체험·훈련 콘텐츠를 제공합니다.
            </Typography>
          </Box>
        </Stack>
      </Container>
      <Container disableGutters>
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={500} sx={{ lineHeight: 1.6 }}>
              HMD(Head Mounted Display), Mobile, PC 등 
              <Box component="span" sx={{ display: 'block' }}>
                다양한 기기 지원이 가능합니다.
              </Box>
            </Typography>
          </Box>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={3} sx={{ mt: 6 }}>
            {platform.map((item, i) => (
              <Box
                key={i}
                ref={(el) => (imgRefs.current[i] = el)}
                role="img"
                aria-label={`${item.title} 기기`}
                sx={{
                  backgroundImage: `url(${item.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: '1',
                  height: isMobile ? 'auto' : '460px',
                  borderRadius: 4,
                  ...(isMobile && {
                    width: '100%',
                    aspectRatio: 1 / 1.28
                  })
                }}
              >
                <Typography variant="h5" color="text.contrastText">{item.title}</Typography>
                <Typography fontWeight={500} color="text.contrastText" sx={{ whiteSpace: 'pre-line', textAlign: 'center', mt: 1 }}>{item.description}</Typography>
              </Box>
            ))}
          </Stack>
      </Container>
      <Container disableGutters sx={{ py: 10 }}>
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: isMobile ? 'auto' : '631px' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: -1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><PlatformMotion /></Box>
          {!isMobile && (
            <Stack alignItems="center" ref={containerRef}>
              {textLines.map((rawText, lineIndex) => {
                // 번역은 문장 단위에서 먼저
                const translatedText = translateText(rawText);

                return (
                  <Typography
                    key={lineIndex}
                    aria-label={translatedText}
                    variant="h4"
                    sx={{ mb: 2, display: 'flex' }}
                  >
                    <span aria-hidden="true">
                      {translatedText.split('').map((char, charIndex) => (
                        <span
                          key={charIndex}
                          ref={(el) => {
                            if (!lettersRef.current[lineIndex]) {
                              lettersRef.current[lineIndex] = [];
                            }
                            lettersRef.current[lineIndex][charIndex] = el;
                          }}
                          style={{
                            color: '#8A949E',
                            display: 'inline-block',
                          }}
                        >
                          {char === ' ' ? '\u00A0' : char}
                        </span>
                      ))}
                    </span>
                  </Typography>
                );
              })}
            </Stack>
          )}
          {isMobile && (
            <Stack alignItems="center" sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={600}>
                대학교 등 고등교육기관에서 
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                필요한 다양한 분야의 
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                콘텐츠를 공유 및 활용이 
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                가능합니다.
              </Typography>
            </Stack>
          )}
        </Box>
      </Container>
      <Container disableGutters>
        <Box ref={featuresRef}>
          <Grid container spacing={3} sx={{ mb: isMobile ? 7 : 17 }}>
            {features.map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card
                  ref={el => (cardsRef.current[i] = el)}
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {item.title}
                    </Typography>
                    <Typography color="grey.600">
                      {item.description}
                    </Typography>
                  </Stack>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Box component="img" src={item.icon} alt="" />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
      <Container disableGutters>
        <Box
          ref={bannerRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: isMobile ? '144px' : '200px',
            borderRadius: 4,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            opacity: isMobile ? 1 : 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${Bg})`,
              backgroundSize: '100% 100%',
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              zIndex: -1,
            }}
          />
          <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} component="p" fontWeight={700} color="text.contrastText" sx={{ mb: isMobile ? 0 : 1.5, ...(isMobile && { textAlign: 'center' }) }}>연결이 만드는 새로운 서비스</Typography>
          <Typography fontWeight={500} color="text.contrastText" sx={{ display: isMobile ? 'none' : 'block' }}>표준 API로 서비스와 데이터를 간편하게 이용해 주세요.</Typography>
        </Box>
      </Container>
    </Box>
  )
}
